/**
 * Resolve a video's session clock to distance along one of the laps represented
 * by that video panel. Candidates are tried in order so a split view can put
 * its reference lap first while a normal full-session video can offer every
 * selected lap.
 */
export function distanceMetersAtVideoTime(videoTime, candidates = []) {
  if (!Number.isFinite(videoTime)) return null;

  for (const candidate of candidates) {
    const startTime = candidate?.startTime;
    const points = candidate?.byTime;
    if (!Number.isFinite(startTime) || !points?.length) continue;

    const lapTime = videoTime - startTime;
    const first = points[0];
    const last = points[points.length - 1];
    // Telemetry commonly begins a fraction after the lap beacon. Let the exact
    // lap start/end land on the nearest sample, without clamping arbitrary
    // session video between or outside the selected laps.
    const endpointTolerance = 0.25;
    if (!Number.isFinite(first?.time) || !Number.isFinite(last?.time)
        || lapTime < first.time - endpointTolerance
        || lapTime > last.time + endpointTolerance) continue;

    if (lapTime <= first.time) return first.dist;
    if (lapTime >= last.time) return last.dist;

    let lo = 0;
    let hi = points.length - 1;
    while (lo + 1 < hi) {
      const mid = (lo + hi) >> 1;
      if (points[mid].time <= lapTime) lo = mid;
      else hi = mid;
    }
    const a = points[lo];
    const b = points[hi];
    const span = b.time - a.time;
    const frac = span > 0 ? (lapTime - a.time) / span : 0;
    return a.dist + frac * (b.dist - a.dist);
  }

  return null;
}

/** Circular frame error: frame zero and the last frame are adjacent in Lap3D. */
export function lap3dFrameError(currentFrame, targetFrame, totalFrames) {
  if (!Number.isFinite(currentFrame) || !Number.isFinite(targetFrame)
      || !(totalFrames > 0)) return Infinity;
  const direct = Math.abs(currentFrame - targetFrame);
  return Math.min(direct, Math.max(0, totalFrames - direct));
}
